import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import AppMockup from '../../assets/farmget_app_mockup.png';
import AppScreenHome from '../../assets/app_screen_home.png';
import AppScreenMap from '../../assets/app_screen_map.png';
import AppScreenDetails from '../../assets/app_screen_details.png';
import AppScreenAdvisory from '../../assets/app_screen_advisory.png';
import {
    BeakerIcon,
    BugAntIcon,
    CloudIcon,
    CameraIcon,
    SunIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const OurApp = () => {
    const { scrollYProgress } = useScroll();
    const y = useTransform(scrollYProgress, [0, 1], [0, -50]);

    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15
            }
        }
    };

    const features = [
        {
            title: "Soil Health Analysis",
            desc: "5 key parameters (N, P, K, Organic Carbon, pH)",
            icon: BeakerIcon
        },
        {
            title: "Pest & Disease Forewarning",
            desc: "Early risk alerts",
            icon: BugAntIcon
        },
        {
            title: "Smart Irrigation Management",
            desc: "Moisture & weather-based scheduling",
            icon: CloudIcon
        },
        {
            title: "Image-Based Advisory",
            desc: "Instant crop diagnosis",
            icon: CameraIcon
        },
        {
            title: "Weather Forecast Integration",
            desc: "Better field planning",
            icon: SunIcon
        },
        {
            title: "Complete Crop Management",
            desc: "INM, IPM, IDM & IWM guidance",
            icon: ChartBarIcon
        }
    ];

    return (
        <div className="bg-white min-h-screen font-sans overflow-x-hidden">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center bg-gray-900 text-white overflow-hidden py-20 lg:py-0">
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-[-20%] left-[-10%] w-[50rem] h-[50rem] bg-green-500/20 rounded-full blur-[120px] animate-pulse"></div>
                    <div className="absolute bottom-[-20%] right-[-10%] w-[40rem] h-[40rem] bg-blue-500/20 rounded-full blur-[100px] animate-pulse delay-1000"></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
                    <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                        <motion.div
                            className="lg:w-1/2 text-center lg:text-left"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                        >
                            <motion.span variants={fadeInUp} className="inline-block py-1 px-3 rounded-full bg-green-500/10 text-green-400 font-bold tracking-widest text-xs uppercase mb-6 border border-green-500/20">
                                Smart Farming. Simplified.
                            </motion.span>
                            <motion.h1 variants={fadeInUp} className="text-5xl lg:text-7xl font-extrabold mb-6 leading-tight">
                                FarmGet <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600">
                                    App
                                </span>
                            </motion.h1>
                            <motion.p variants={fadeInUp} className="text-lg lg:text-xl text-gray-400 mb-4 max-w-lg mx-auto lg:mx-0 leading-relaxed font-light">
                                <strong className="text-gray-200">FarmGet</strong> is an all-in-one digital agriculture platform designed to turn farm data into clear, actionable decisions.
                            </motion.p>
                            <motion.p variants={fadeInUp} className="text-lg lg:text-xl text-gray-400 mb-10 max-w-lg mx-auto lg:mx-0 leading-relaxed font-light">
                                It empowers farmers with real-time intelligence to improve yield, reduce risk, and maximize profitability.
                            </motion.p>

                            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <a
                                    href="https://play.google.com/store/apps/details?id=com.marvelous.farmget"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group flex items-center justify-center gap-3 bg-white text-gray-900 px-6 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 transform hover:-translate-y-1 shadow-xl"
                                >
                                    <svg className="w-8 h-8 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 21,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                                    </svg>
                                    <div className="text-left">
                                        <div className="text-[10px] uppercase font-bold tracking-wider opacity-60">Get it on</div>
                                        <div className="line-height-none">Google Play</div>
                                    </div>
                                </a>
                                <a
                                    href="https://apps.apple.com/in/app/farmget/id6752594841"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group flex items-center justify-center gap-3 bg-transparent border border-gray-700 text-white px-6 py-3 rounded-xl font-bold hover:bg-white/5 transition-all duration-300 transform hover:-translate-y-1 backdrop-blur-sm"
                                >
                                    <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.04,6.7 11.95,6.61C11.8,5.37 12.36,4.26 13,3.5Z" />
                                    </svg>
                                    <div className="text-left">
                                        <div className="text-[10px] uppercase font-bold tracking-wider opacity-60">Download on the</div>
                                        <div className="line-height-none">App Store</div>
                                    </div>
                                </a>
                            </motion.div>
                        </motion.div>

                        <motion.div
                            className="lg:w-1/2 relative z-10"
                            initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
                            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                            transition={{ duration: 0.8 }}
                            style={{ y }}
                        >
                            <div className="relative mx-auto w-full max-w-[350px]">
                                <div className="absolute inset-0 bg-green-500 rounded-full blur-[60px] opacity-20 animate-pulse"></div>
                                <img
                                    src={AppMockup}
                                    alt="FarmGet App Interface"
                                    className="relative z-10 w-full h-auto drop-shadow-2xl"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Screenshots Gallery Section */}
            <section className="py-24 bg-white relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="text-center mb-16"
                    >
                        <motion.span variants={fadeInUp} className="text-green-500 font-bold tracking-widest uppercase text-sm">In-App Experience</motion.span>
                        <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-light text-gray-900 mt-2">
                            Visualizing <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600">Smart Agriculture</span>
                        </motion.h2>
                    </motion.div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                            { title: "Dashboard", img: AppScreenHome },
                            { title: "Satellite Mapping", img: AppScreenMap },
                            { title: "Farm Analytics", img: AppScreenDetails },
                            { title: "Advisory", img: AppScreenAdvisory }
                        ].map((screen, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1, duration: 0.5 }}
                                className="group text-center"
                            >
                                <div className="relative rounded-[2rem] border-4 border-gray-900 overflow-hidden shadow-2xl transition-transform duration-300 group-hover:-translate-y-4">
                                    <img src={screen.img} alt={screen.title} className="w-full h-auto" />
                                </div>
                                <h3 className="mt-6 text-lg font-bold text-gray-900 group-hover:text-green-600 transition-colors">{screen.title}</h3>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                            🌾 What FarmGet Delivers
                        </h2>
                    </div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                    >
                        {features.map((feature, idx) => (
                            <motion.div
                                key={idx}
                                variants={fadeInUp}
                                className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:border-green-300 hover:shadow-xl transition-all group"
                            >
                                <div className="bg-green-50 w-14 h-14 rounded-xl flex items-center justify-center mb-6 text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors duration-300">
                                    <feature.icon className="w-8 h-8" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                                <div className="flex items-start gap-2">
                                    <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <p className="text-gray-600 font-medium">{feature.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Why FarmGet? */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="bg-gray-900 p-10 md:p-16 rounded-3xl shadow-2xl text-white relative overflow-hidden"
                    >
                        <div className="absolute -right-20 -top-20 w-80 h-80 bg-green-500/20 rounded-full blur-3xl text-white"></div>

                        <h3 className="text-3xl md:text-4xl font-bold mb-10 flex items-center gap-3 relative z-10 text-emerald-400">
                            🚀 Why FarmGet?
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative z-10 mb-12 border-b border-gray-800 pb-12">
                            <div className="md:col-span-1">
                                <p className="text-2xl font-light text-gray-300 leading-relaxed space-y-2">
                                    <span className="block line-through text-gray-600 mb-2 decoration-red-500/50 decoration-2">No guesswork.</span>
                                    <span className="block line-through text-gray-600 decoration-red-500/50 decoration-2">No blind decisions.</span>
                                </p>
                            </div>
                            <div className="md:col-span-2">
                                <p className="text-2xl font-medium text-emerald-50 leading-relaxed border-l-4 border-emerald-500 pl-6">
                                    Only data-driven, field-specific recommendations designed for <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300">Healthy & Wealthy Agriculture</span>.
                                </p>
                            </div>
                        </div>

                        <div className="relative z-10 text-center md:text-left">
                            <p className="text-xl text-gray-300 font-light mb-8 max-w-3xl">
                                FarmGet transforms farming into a smart, measurable, and profitable system — all from one powerful app.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                                <div className="bg-emerald-500/10 border border-emerald-500/30 px-6 py-4 rounded-xl">
                                    <p className="text-emerald-300 font-bold tracking-wide">Precision in Every Decision.</p>
                                </div>
                                <div className="bg-emerald-500/10 border border-emerald-500/30 px-6 py-4 rounded-xl">
                                    <p className="text-emerald-300 font-bold tracking-wide">Performance in Every Harvest.</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <div className="mt-20 text-center relative z-10">
                        <p className="text-gray-400 text-sm tracking-widest uppercase">
                            Empowering farmers with technology • Marvelous Agri Solution
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default OurApp;
